import React from 'react';
import { Box, Button, HStack, Menu, Portal, Text } from '@chakra-ui/react';
import { FiDownload, FiMoreVertical } from 'react-icons/fi';
import ViewerExpandButton from './shared/ViewerExpandButton';
import ImageDocumentRenderer from './shared/ImageDocumentRenderer';
import ViewerFullScreenDialog from './ViewerFullScreenDialog.tsx';
import { preventScrolling } from '@/utils/preventScrolling.ts';
import { COMPACT_VIEWER_HIGHLIGHT_BG } from './shared/types';

const DOCUMENT_PREVIEW_ASPECT = 1.414;

interface ImageViewerProps {
	fileUrl: string;
	title?: string;
	mode?: 'full' | 'compact';
	maxWidth?: number;
	showExpandButton?: boolean;
	showExternalLink?: boolean;
	disableHoverEffects?: boolean;
	isHighlighted?: boolean;
	showPreviewHeader?: boolean;
	unstyledCompact?: boolean;
}

/**
 * Simple image viewer for file previews.
 * Keeps parity with PdfViewer API for easy routing via FileViewer.
 */
const ImageViewer: React.FC<ImageViewerProps> = ({
	fileUrl,
	title,
	mode = 'full',
	maxWidth = 300,
	showExpandButton = true,
	showExternalLink = false,
	disableHoverEffects = false,
	isHighlighted = false,
	showPreviewHeader = true,
	unstyledCompact = false,
}) => {
	const [isExpanded, setIsExpanded] = React.useState(false);
	const onClose = () => setIsExpanded(false);
	const openInNewTab = () => window.open(fileUrl, '_blank', 'noopener,noreferrer');

	if (mode === 'compact') {
		return (
			<Box
				maxW={maxWidth}
				bg={unstyledCompact ? 'transparent' : isHighlighted ? COMPACT_VIEWER_HIGHLIGHT_BG : 'gray.50'}
				borderRadius={unstyledCompact ? 0 : '2xl'}
				transition="box-shadow 0.15s ease, transform 0.15s ease"
				_hover={
					disableHoverEffects || unstyledCompact ? {} : { boxShadow: 'md', transform: 'translateY(-1px)' }
				}
				my={0}
				overflow="hidden"
			>
				{showPreviewHeader && (
					<Box px={3} py={2} onMouseDown={preventScrolling}>
						<HStack justify="space-between" fontSize="sm">
							<Text fontWeight="medium" color="brand.fg" truncate lineClamp={2}>
								{title ?? 'Image Preview'}
							</Text>
							{showExternalLink && (
								<Menu.Root positioning={{ placement: 'right-start' }}>
									<Menu.Trigger asChild>
										<Button
											p={0}
											m={0}
											_hover={disableHoverEffects ? {} : { bg: 'brand.muted' }}
											alignSelf="start"
											size="xs"
											rounded="full"
											variant="ghost"
											title="More options"
											onMouseDown={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
										>
											<FiMoreVertical />
										</Button>
									</Menu.Trigger>
									<Portal>
										<Menu.Positioner>
											<Menu.Content minW="120px">
												<Menu.Item value="download-file" onClick={openInNewTab}>
													<FiDownload />
													Download
												</Menu.Item>
											</Menu.Content>
										</Menu.Positioner>
									</Portal>
								</Menu.Root>
							)}
						</HStack>
					</Box>
				)}

				{/* Content */}
				<Box
					p={unstyledCompact ? 0 : 2}
					h={unstyledCompact ? maxWidth * DOCUMENT_PREVIEW_ASPECT : maxWidth * 0.8}
					overflow="hidden"
					onMouseDown={preventScrolling}
					className="group"
					position="relative"
					cursor="pointer"
				>
					{/* Expand Button */}
					{showExpandButton && (
						<ViewerExpandButton onExpand={() => setIsExpanded(true)} loading={false} top={3} right={5} />
					)}

					<Box
						onClick={() => setIsExpanded(true)}
						_hover={{ cursor: 'pointer' }}
						px={unstyledCompact ? 0 : 2}
					>
						<ImageDocumentRenderer
							src={fileUrl}
							containerProps={{
								h: 'auto',
								w: '100%',
								borderRadius: 'sm',
								overflow: 'hidden',
							}}
							spinnerSize="md"
						/>
					</Box>
				</Box>

				{isExpanded && (
					<ViewerFullScreenDialog
						fileKind="image"
						src={fileUrl}
						title={title ?? 'Image Viewer'}
						isOpen={isExpanded}
						onClose={onClose}
					/>
				)}
			</Box>
		);
	}

	// full mode
	return (
		<Box
			w="full"
			borderRadius="md"
			border="1px solid"
			borderColor="border.default"
			overflow="hidden"
			bg="surface.subtle"
			p={4}
			position="relative"
			className="group"
		>
			{showExpandButton && (
				<ViewerExpandButton onExpand={() => setIsExpanded(true)} loading={false} top={2} right={2} />
			)}
			{/* The click sits on the image, not the outer Box: the dialog below is a React child of
			    that Box, and portalled clicks still bubble through the React tree — closing it would
			    immediately reopen it. Matches the compact variant above. */}
			<Box onClick={() => setIsExpanded(true)} cursor="pointer">
				<ImageDocumentRenderer src={fileUrl} containerProps={{ h: 'auto', w: '100%' }} spinnerSize="lg" />
			</Box>
			{isExpanded && (
				<ViewerFullScreenDialog
					fileKind="image"
					src={fileUrl}
					title={title ?? 'Image Viewer'}
					isOpen={isExpanded}
					onClose={onClose}
				/>
			)}
		</Box>
	);
};

export default ImageViewer;
